/**
 * RankCell — renders current rank with a tiny "previous / best" stack.
 *
 * Props:
 *   rank            (int)    current rank in this leaderboard scope
 *   previousRank    (int|null) end-of-last-matchweek rank; null when no snapshot exists yet
 *   bestRank        (int|null) best (lowest number) rank this season
 *   icon            (node|null) trophy/medal icon to show inline
 *
 * Prev-rank chip shows a delta arrow — green up when the current rank number
 * decreased (moved up the table), red down when increased. Best-rank chip is
 * only shown when it's better than the current rank.
 */
import React from "react";
import { ArrowUp, ArrowDown, Trophy } from "@phosphor-icons/react";

export function RankCell({ rank, previousRank, bestRank, icon = null }) {
  const hasPrev = previousRank != null;
  const delta = hasPrev ? previousRank - rank : 0; // positive = climbed
  const bestBeatsCurrent = bestRank != null && bestRank < rank;

  return (
    <div className="flex flex-col items-start gap-1" data-testid="rank-cell">
      <div className="flex items-center gap-1.5">
        {icon}
        <span
          className="font-mono-num text-lg font-bold text-white/80"
          data-testid="rank-current"
        >
          #{rank}
        </span>
      </div>
      {hasPrev && (
        <div
          className="flex items-center gap-0.5 text-[10px] uppercase tracking-widest text-white/40 font-mono-num"
          data-testid="rank-previous"
          title={`Previous position: #${previousRank}`}
        >
          <span>prev #{previousRank}</span>
          {delta > 0 && (
            <span className="text-emerald-400 flex items-center" data-testid="rank-delta-up">
              <ArrowUp size={10} weight="bold" />
              {delta}
            </span>
          )}
          {delta < 0 && (
            <span className="text-red-400 flex items-center" data-testid="rank-delta-down">
              <ArrowDown size={10} weight="bold" />
              {Math.abs(delta)}
            </span>
          )}
          {delta === 0 && <span className="text-white/30">—</span>}
        </div>
      )}
      {bestBeatsCurrent && (
        <div
          className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-primary-yellow font-mono-num"
          data-testid="rank-best"
          title={`Season best position: #${bestRank}`}
        >
          <Trophy size={10} weight="fill" />
          <span>best #{bestRank}</span>
        </div>
      )}
    </div>
  );
}

export default RankCell;
